import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { CaretDown, CaretRight } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import {
  getLibraryItemGroupCode,
  getOrderedLibraryGroupCodes,
  LIBRARY_GROUP_ORDER,
  MIN_LIBRARY_SEARCH_LENGTH,
  normalizeLibrarySearchText
} from '../../utils/librarySearch';
import LibraryEmptyState from './LibraryEmptyState';

const GROUP_TRANSLATION_KEYS = {
  day: 'daysDisplay',
  additional: 'additionalContent',
  HTL: 'serviceTypeAccommodation',
  ACT: 'serviceTypeActivity',
  CAR: 'serviceTypeCar',
  FLT: 'serviceTypeFlight',
  INF: 'serviceTypeInfo',
  RST: 'serviceTypeRestaurant',
  TRN: 'serviceTypeTrain',
  TRF: 'serviceTypeTransfer'
};

const groupItems = (items) =>
  items.reduce((groups, item) => {
    const code = getLibraryItemGroupCode(item);
    if (!code) return groups;
    groups[code] = [...(groups[code] || []), item];
    return groups;
  }, {});

const LibraryResults = ({
  items,
  query,
  selectedTypes,
  onSelectTypes,
  onClearSearch,
  onResultsChange,
  renderItem
}) => {
  const { t } = useTranslation('library');
  const [collapsedGroups, setCollapsedGroups] = useState(() => new Set());
  const searchResults = items;
  const resultGroups = useMemo(
    () => groupItems(searchResults),
    [searchResults]
  );
  const orderedGroupCodes = useMemo(
    () => getOrderedLibraryGroupCodes(searchResults, query),
    [searchResults, query]
  );
  const visibleItems =
    selectedTypes.length === LIBRARY_GROUP_ORDER.length
      ? searchResults
      : searchResults.filter((item) =>
          selectedTypes.includes(getLibraryItemGroupCode(item))
        );
  const selectedType = selectedTypes[0];
  const shouldGroupResults =
    selectedTypes.length !== 1 || collapsedGroups.has(selectedType);
  const activeQuery =
    normalizeLibrarySearchText(query).length >= MIN_LIBRARY_SEARCH_LENGTH
      ? query.trim()
      : '';
  const visibleGroupCodes = useMemo(
    () =>
      orderedGroupCodes.filter(
        (code) =>
          resultGroups[code]?.length &&
          (selectedTypes.length === LIBRARY_GROUP_ORDER.length ||
            selectedTypes.includes(code))
      ),
    [orderedGroupCodes, resultGroups, selectedTypes]
  );

  useEffect(() => {
    setCollapsedGroups(new Set());
  }, [query]);

  useEffect(() => {
    const visibleCodes = new Set(visibleGroupCodes);
    setCollapsedGroups((current) => {
      const next = new Set(
        [...current].filter((code) => visibleCodes.has(code))
      );
      return next.size === current.size ? current : next;
    });
  }, [visibleGroupCodes]);

  useEffect(() => {
    onResultsChange?.();
  }, [query, selectedTypes, onResultsChange]);

  const toggleGroup = (code) => {
    setCollapsedGroups((current) => {
      const next = new Set(current);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  return (
    <>
      <div className='library-results-toolbar'>
        <p className='library-result-count'>
          <strong>{visibleItems.length}</strong>{' '}
          {activeQuery
            ? t('itemsForSearchLabel', {
                count: visibleItems.length,
                query: activeQuery
              })
            : t('itemsLabel', { count: visibleItems.length })}
        </p>
      </div>

      {visibleItems.length === 0 ? (
        <LibraryEmptyState
          query={activeQuery}
          noTypesSelected={selectedTypes.length === 0}
          filterLabel={
            selectedTypes.length === 1
              ? t(GROUP_TRANSLATION_KEYS[selectedType])
              : ''
          }
          hasActiveFilters={
            selectedTypes.length > 0 &&
            selectedTypes.length < LIBRARY_GROUP_ORDER.length
          }
          onClear={onClearSearch}
          onClearFilters={() => onSelectTypes([...LIBRARY_GROUP_ORDER])}
          onSelectAllTypes={() => onSelectTypes([...LIBRARY_GROUP_ORDER])}
        />
      ) : !shouldGroupResults ? (
        <div className='library-filtered-results'>
          <ul className='library-result-items'>
            {visibleItems.map((item, index) => renderItem(item, index))}
          </ul>
        </div>
      ) : (
        <div className='library-grouped-results'>
          {visibleGroupCodes.map((code) => {
            const isCollapsed = collapsedGroups.has(code);
            const groupedItems = resultGroups[code];
            const groupName = t(GROUP_TRANSLATION_KEYS[code]);
            return (
              <section className='library-result-group' key={code}>
                <button
                  type='button'
                  className='library-result-group__header'
                  aria-expanded={!isCollapsed}
                  aria-label={t(isCollapsed ? 'expandGroup' : 'collapseGroup', {
                    group: groupName
                  })}
                  onClick={() => toggleGroup(code)}
                >
                  <span className='library-result-group__name'>
                    {groupName}
                  </span>
                  <span className='library-result-group__count'>
                    {groupedItems.length}
                  </span>
                  <span className='library-result-group__caret'>
                    {isCollapsed ? (
                      <CaretRight size={16} aria-hidden='true' />
                    ) : (
                      <CaretDown size={16} aria-hidden='true' />
                    )}
                  </span>
                </button>
                {!isCollapsed && (
                  <ul className='library-result-items'>
                    {groupedItems.map((item, index) => renderItem(item, index))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
};

LibraryResults.propTypes = {
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  query: PropTypes.string.isRequired,
  selectedTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSelectTypes: PropTypes.func.isRequired,
  onClearSearch: PropTypes.func.isRequired,
  onResultsChange: PropTypes.func,
  renderItem: PropTypes.func.isRequired
};

export default LibraryResults;
